// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useRef } from 'react';

import type { SpoolWithModel, EntityAttachment, Project } from '@evolvemep/api-ts';
import { Flex, ActionIcon, Text, Loader, Card, Stack, Divider } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { ForgeViewerFromAttachment } from 'components/Autodesk/ForgeViewerFromAttachment';
import { ForgeViewerFromModel } from 'components/Autodesk/ForgeViewerFromModel';
import {
  AutodeskExtensionStoreProvider,
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import {
  type AttachmentListProps,
  type ViewerOwnerKey,
  AttachmentList,
} from 'components/Mantine/Attachments/AttachmentList';
import { WrappedModal, useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { withHttp } from 'helpers/common';
import { isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { ModelSelector } from 'modules/Shop/Tasks/TaskViewer/ModelSelector';

export type ViewingAttachment = (
  | {
      attachment: EntityAttachment<never>;
      rowNum: number;
    }
  | {
      urn: string;
      isSpoolModel: boolean;
    }
) & {
  /** The `AttachmentList` instance that opened the viewer; gates which list reopens it from storage. */
  owner: ViewerOwnerKey;
};

type Props = AttachmentListProps & {
  project?: Pick<Project, 'projectId' | 'projectName'>;
  opened: boolean;
  onClose: () => void;
  spool?: SpoolWithModel;
  loading?: boolean;
  defaultSelection?: ViewingAttachment;
  owner: ViewerOwnerKey;
};

const FullScreenAttachmentViewerContents = ({
  spool,
  loading,
  project,
  defaultSelection,
  onAttachmentCountRealized,
  onClose,
  owner,
  ...props
}: Props) => {
  const { setOnClose } = useWrappedModal();

  // Cache the selection such that if we redirect to Autodesk auth,
  // we reopen the last opened attachment upon our return
  const [selection, setSelection] = useLocalStorage('VIEWING_ATTACHMENT', defaultSelection);
  // Store the currently-displayed attachment's row index in a ref to guard against race conditions.
  // -1 means no attachment is currently displayed (e.g. a model/URN is selected, or nothing is).
  const rowIndexRef = useRef<number>(isNotNil(selection) && 'rowNum' in selection ? selection.rowNum : -1);

  // Clear selection on close
  useEffect(() => () => setSelection(undefined), [setSelection]);

  const { setPromptAction } = useAutodeskExtensionStoreActions('viewerMarkup');
  const { hasUnsavedChanges: hasUnsavedMarkupChanges, promptAction } =
    useAutodeskExtensionStoreSnapshot('viewerMarkup');
  useBlockerWhen('pdf-markup', hasUnsavedMarkupChanges);

  const handleDiscardConfirm = useCallback(() => {
    promptAction?.();
    setPromptAction(null);
  }, [promptAction, setPromptAction]);

  const handleDiscardCancel = useCallback(() => {
    setPromptAction(null);
  }, [setPromptAction]);

  /** Runs `action` immediately if there are no unsaved markup changes, otherwise shows the discard confirmation first. */
  const confirmAction = useCallback(
    (action: () => void) => {
      if (!hasUnsavedMarkupChanges) {
        action();
      } else {
        setPromptAction(action);
      }
    },
    [hasUnsavedMarkupChanges, setPromptAction],
  );

  // Override the outer modal's close button to guard against discarding unsaved markup changes.
  useEffect(() => {
    setOnClose(() => () => confirmAction(onClose));
    return () => setOnClose(null);
  }, [confirmAction, onClose, setOnClose]);

  const onAttachmentSelected = useCallback(
    (attachment: EntityAttachment<never>, rowNum: number, force = false) => {
      if (force) {
        // Do nothing if the currently opened attachment is selected by the user.
        if (rowNum === rowIndexRef.current) return;
        // User clicked a different attachment, confirm before discarding any unsaved markup changes.
        confirmAction(() => {
          rowIndexRef.current = rowNum;
          setSelection({ attachment, rowNum, owner });
        });
      } else if (rowNum === rowIndexRef.current) {
        // Called via onRowRealized from AttachmentList to resolve the attachment data for the selected row.
        // This is not user navigation, so there's no need to guard it.
        rowIndexRef.current = rowNum;
        setSelection({ attachment, rowNum, owner });
      }
    },
    [confirmAction, owner, setSelection],
  );

  const onUrnSelected = useCallback(
    (urn: string, isSpoolModel = false) => {
      confirmAction(() => {
        // A model is now displayed, so no attachment row is active. Clearing this ensures a
        // subsequent click on the attachment at the previously-active row still re-selects it.
        rowIndexRef.current = -1;
        setSelection({ urn, isSpoolModel, owner });
      });
    },
    [confirmAction, owner, setSelection],
  );

  const modelSelected = isNotNil(selection) && 'urn' in selection;
  const attachmentSelected = isNotNil(selection) && 'attachment' in selection;
  const fileSelected = attachmentSelected && 'file' in selection.attachment;

  const [totalAttachments, setTotalAttachments] = useUncontrolled({
    defaultValue: 0,
    onChange: onAttachmentCountRealized,
  });

  return (
    <>
      <Flex flex={1} style={{ overflow: 'hidden' }}>
        <Stack miw={400} w={400} h="100%" p="xs" style={{ overflow: 'hidden' }}>
          <Stack flex={1} style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            {isNotNil(project) && (
              <ModelSelector
                spool={spool}
                loadingSpool={loading}
                project={project}
                onUrnSelected={onUrnSelected}
                selectedUrn={modelSelected ? selection.urn : undefined}
              />
            )}

            <AttachmentList
              onFilesUploaded={(fileIds) => {
                const selectedFileId =
                  isNotNil(selection) && 'attachment' in selection && 'file' in selection.attachment
                    ? selection.attachment.file.fileId
                    : null;
                if (!selectedFileId) return;

                if (fileIds.some((id) => id === selectedFileId)) {
                  // In this situation, the user uploaded a new document
                  // replacing the one that was already loaded.
                  // So, now that we finished uploading the new document,
                  // deselect the existing document
                  // and then reselect it so it loads the latest version.
                  setSelection(undefined);
                  const cachedSelection = selection;
                  setTimeout(() => setSelection(cachedSelection));
                }
              }}
              {...(attachmentSelected
                ? {
                    selectedRow: selection.rowNum,
                    onRowRealized: onAttachmentSelected,
                  }
                : {})}
              onAttachmentSelected={(attachment, rowNum) => onAttachmentSelected(attachment, rowNum, true)}
              autoloadRemainingAttachments
              onAttachmentCountRealized={setTotalAttachments}
              pageSize={50}
              {...props}
            />
          </Stack>
        </Stack>

        <Divider orientation="vertical" />

        <Card radius={0} style={{ width: '100%' }} p={0}>
          {modelSelected ? (
            <AutodeskExtensionStoreProvider storeName="apsViewer">
              <AutodeskExtensionStoreProvider storeName="spools">
                <AutodeskExtensionStoreProvider storeName="elementTags" opts={{ initialVisible: true }}>
                  <AutodeskExtensionStoreProvider storeName="dimensioning">
                    <ForgeViewerFromModel
                      urn={selection.urn}
                      shouldInitializeAuth
                      autoSelectSpool={selection.isSpoolModel ? spool?.name : undefined}
                    />
                  </AutodeskExtensionStoreProvider>
                </AutodeskExtensionStoreProvider>
              </AutodeskExtensionStoreProvider>
            </AutodeskExtensionStoreProvider>
          ) : attachmentSelected ? (
            <ForgeViewerFromAttachment
              headerContents={
                <Flex gap="md" align="center">
                  <AgGridStyleTooltip label="Previous attachment" withArrow>
                    <ActionIcon
                      size="sm"
                      color="gray"
                      onClick={() => {
                        confirmAction(() => {
                          rowIndexRef.current -= 1;
                          if (rowIndexRef.current < 0) {
                            rowIndexRef.current = totalAttachments - 1;
                          }
                          setSelection({
                            ...selection,
                            rowNum: rowIndexRef.current,
                          });
                        });
                      }}
                      disabled={totalAttachments === 0}
                    >
                      <EvolveIcon
                        icon="ArrowDown"
                        size="xs"
                        spanProps={{
                          style: {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      />
                    </ActionIcon>
                  </AgGridStyleTooltip>
                  <AgGridStyleTooltip label="Next attachment" withArrow>
                    <ActionIcon
                      size="sm"
                      color="gray"
                      onClick={() => {
                        confirmAction(() => {
                          rowIndexRef.current += 1;
                          if (rowIndexRef.current >= totalAttachments) {
                            rowIndexRef.current -= totalAttachments;
                          }
                          setSelection({
                            ...selection,
                            rowNum: rowIndexRef.current,
                          });
                        });
                      }}
                      disabled={totalAttachments === 0}
                    >
                      <EvolveIcon icon="ArrowDown" size="xs" />
                    </ActionIcon>
                  </AgGridStyleTooltip>
                </Flex>
              }
              attachment={fileSelected ? selection.attachment : undefined}
              noDocumentSelected={
                'webLink' in selection.attachment ? (
                  <>
                    <Text>External URL selected, click to follow.</Text>
                    <Text
                      component="a"
                      href={withHttp(selection.attachment.webLink.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="link"
                    >
                      {selection.attachment.webLink.url}
                    </Text>
                  </>
                ) : (
                  <>{(loading || isNotNil(spool)) && <Loader />}</>
                )
              }
            />
          ) : null}
        </Card>
      </Flex>

      <WrappedModal
        title="Unsaved file markup changes"
        titleIcon="Alert"
        titleIconColor="red"
        opened={isNotNil(promptAction)}
        onClose={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        confirmationText="Discard changes"
        cancellationText="Keep editing"
        withCancelButton
        buttonColor="red"
      >
        <Text>Are you sure you want to continue? Any unsaved changes to the file&apos;s markup will be lost.</Text>
      </WrappedModal>
    </>
  );
};

export const FullScreenAttachmentViewer = (props: Props) => (
  <AutodeskExtensionStoreProvider storeName="viewerMarkup">
    <WrappedModal
      title="Attachments"
      fullScreen
      hideModalButtons
      opened={props.opened}
      onClose={props.onClose}
      bodyProps={{
        flex: 1,
        p: 0,
      }}
    >
      <FullScreenAttachmentViewerContents {...props} />
    </WrappedModal>
  </AutodeskExtensionStoreProvider>
);
